import { UseFormReturn } from 'react-hook-form';
import { ValueType } from '@srm/ui/types/ValueType';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { IDocumentData } from '@models/document/IDocumentData';
import { IFieldElem } from '@models/IFormData';
import type { IDocumentTable } from '@models/Forms/IForms';
import { collectValidatableControls, collectInvokeChangeControls, collectChangeControls, InvokeChangeControl } from './schemeWalk';
import { IFormulaEvaluator } from './formulaEvaluator';
import { ChangesFieldValue, type SaveChangeField } from '@utils/managers/ChangesManager';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { getFieldBindingKey, isDocumentFieldKey, type ChangeRuntime } from '@utils/managers/ChangeRuntime';

/**
 * Проставить `0` пустым (null/undefined) Boolean-полям независимо от маунта их вкладки.
 *
 * Идемпотентно (повтор для уже заполненного поля — no-op), поэтому безопасно сосуществует с
 * per-field эффектом Field.tsx. `shouldDirty: true` сохраняет прежнюю семантику этого эффекта.
 */
export function initBooleanDefaults(
    scheme: IDocumentScheme,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<IDocumentData>,
    opts: { isNew: boolean; isEdit: boolean },
): void {
    if (!opts.isNew && !opts.isEdit) return;

    for (const control of collectValidatableControls(scheme)) {
        if (control.kind !== 'field' || control.field.valueType !== ValueType.Boolean) continue;
        const elem = fields[control.key];
        if (!elem || elem.index === undefined || elem.index < 0) continue;

        const current = formMethods.getValues(elem.name as never);
        if (current === null || current === undefined) {
            formMethods.setValue(elem.name as never, 0 as never, { shouldDirty: true });
        }
    }
}

export interface IInvokeChangesInitOptions {
    isNew: boolean;
    isEdit: boolean;
    /** turnOffInvokeChangesOnNew — глушит invokeChangesOnNew (черновик/после сабмита). */
    turnOff: boolean;
    /** Запись set'а в форму (useFieldSaver) — тот же onSave, что у Field/Dict/Abook. */
    onSave: SaveChangeField;
    setError: (errors?: string[]) => void;
    /** Защита от продолжения каскада после переключения на другой документ. */
    isCurrent?: () => boolean;
    /**
     * Каскад контрола выполнен (или начал выполняться) централизованно. Заполняет реестр
     * CentralizedInitExecutedKeysContext: контролы БЕЗ ключа в реестре выполняют каскад сами
     * при первом видимом маунте после initComplete (скрытые на инициализации).
     */
    onExecuted?: (key: string) => void;
    /** Тот же runtime, что используется будущими/поздно монтируемыми контролами формы. */
    changeRuntime?: ChangeRuntime;
}

/**
 * Предохранитель от «мигающих» правил видимости: повторные свипы по так и не раскрывшимся
 * контролам прекращаются либо когда очередной свип никого не раскрыл, либо по этому лимиту.
 */
const SWEEP_LIMIT = 5;

/**
 * Один раз последовательно запускает стартовые каскады видимых контролов.
 * Последовательность важна: изменения предыдущего контрола должны быть доступны следующему.
 * Операция не идемпотентна, поэтому mount-эффекты контролов при этом отключаются контекстом.
 *
 * Каскад может раскрыть видимость контрола, который проход уже миновал как скрытый
 * (в т.ч. выше по схеме), поэтому по так и не раскрывшимся контролам выполняются
 * повторные свипы, пока очередной свип раскрывает хоть один (не дольше SWEEP_LIMIT).
 */
export async function runInvokeChangesInit(
    scheme: IDocumentScheme,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<IDocumentData>,
    evaluator: IFormulaEvaluator,
    opts: IInvokeChangesInitOptions,
): Promise<void> {
    if (!opts.isNew && !opts.isEdit) return;

    // Отменённый (сменой документа) каскад больше не пишет в форму и не поднимает ошибки.
    const isActive = () => opts.isCurrent?.() !== false;
    const sourceMethods = formMethods;
    formMethods = { ...sourceMethods, setValue: (...args) => {
        if (isActive()) sourceMethods.setValue(...args);
    } };
    const saveField: SaveChangeField = (...args) =>
        isActive() ? opts.onSave(...args) : { saved: false, value: undefined };
    const saveTableRow = saveField;
    const reportErrors = (errors?: string[]) => {
        if (isActive()) opts.setError(errors);
    };
    const activeTargets = new Set<string>();
    const changeControls = collectChangeControls(scheme);
    const controlsByKey = new Map(changeControls.map((control) => [control.key, control]));
    const tableControls: Array<{ table: IDocumentTable; rootKey: string; visibilityChain: string[] }> = [];
    const collectTables = (table: IDocumentTable, rootKey: string, visibilityChain: string[]) => {
        const chain = table.visibilityRules ? [...visibilityChain, table.visibilityRules] : visibilityChain;
        tableControls.push({ table, rootKey, visibilityChain: chain });
        for (const child of table.tables ?? []) collectTables(child, rootKey, chain);
    };
    for (const control of changeControls) {
        if (control.table) collectTables(control.table, control.key, control.visibilityChain);
    }
    const invokeSavedChange = async (
        binding: { key: string; tableKey?: string; rowId?: string; scopeId?: string },
        value: any,
        rowData?: any,
        active?: Set<string>,
        rowOldData?: any,
    ) => {
        if (!isActive()) return false;
        const invocationTargets = active ?? activeTargets;
        const scopedBinding = {
            ...binding,
            scopeId: isDocumentFieldKey(binding.key) ? undefined : binding.scopeId ?? (binding.tableKey ? fields[binding.tableKey]?.name : undefined),
        };
        if (opts.changeRuntime?.hasHandler(scopedBinding)) {
            return opts.changeRuntime.invoke(scopedBinding, value, { activeTargets: invocationTargets, scopeId: scopedBinding.scopeId, isCurrent: isActive }, rowData, rowOldData);
        }
        const targetId = getFieldBindingKey(scopedBinding);
        if (invocationTargets.has(targetId)) return false;
        if (binding.tableKey && rowData) {
            const target = tableControls.find(({ table, rootKey }) => table.key === binding.tableKey &&
                (!binding.scopeId || binding.scopeId === fields[rootKey]?.name || binding.scopeId.startsWith(`${fields[rootKey]?.name}.`)));
            if (!target || fields[target.rootKey]?.pending || fields[target.rootKey]?.loadFailed) return false;
            // Используем тот же saver/вычислитель колонки, что и EditTable; вкладка ещё может быть не смонтирована.
            const [{ createTableFormBridge }, { collectTableColumns }] = await Promise.all([
                import('@molecules/formbuilder/controls/Table/controls/tableFormBridge'),
                import('@molecules/formbuilder/controls/Table/controls/tableColumns'),
            ]);
            if (!isActive()) return false;
            const bridge = createTableFormBridge({ fields, formMethods, isEdit: opts.isEdit, isNew: opts.isNew,
                setError: reportErrors, changeRuntime: opts.changeRuntime, invokeSavedChange,
                isCurrent: isActive,
                scopeId: scopedBinding.scopeId ?? fields[target.rootKey]?.name });
            invocationTargets.add(targetId);
            try {
                for (const rule of target.visibilityChain) {
                    if (!(await bridge.evalRowRule(rule, rowData))) return false;
                }
                for (const { column } of collectTableColumns(target.table)) {
                    if (column.key !== binding.key || !('changes' in column)) continue;
                    if (!(await bridge.evalRowRule(column.visibilityRules, rowData))) continue;
                    if (!isActive()) return false;
                    await bridge.onChangeCellValue(rowData, rowOldData ?? rowData, column, target.table, false,
                        { activeTargets: invocationTargets, scopeId: scopedBinding.scopeId });
                    return true;
                }
                return false;
            } finally {
                invocationTargets.delete(targetId);
            }
        }
        const control = controlsByKey.get(binding.key);
        const elem = fields[binding.key];
        if (!control?.changes || !elem || elem.pending || elem.loadFailed || invocationTargets.has(targetId)) return false;
        invocationTargets.add(targetId);
        try {
            for (const rule of control.visibilityChain) {
                if (!(await evaluator.evalBool(rule, true))) return false;
            }
            if (control.visibilityRules && !(await evaluator.evalBool(control.visibilityRules, true))) return false;
            if (!isActive()) return false;
            await ChangesFieldValue(
                control.changes, value, binding.key, fields, opts.isEdit, opts.isNew,
                control.table ? saveTableRow : saveField, reportErrors, control.table, rowData, formMethods, rowOldData,
                invokeSavedChange, invocationTargets, opts.changeRuntime?.actions,
            );
            return true;
        } finally {
            invocationTargets.delete(targetId);
        }
    };

    const runCascade = async (control: InvokeChangeControl): Promise<void> => {
        const elem = fields[control.key];
        const value = formMethods.getValues(elem.name as never);
        if (control.table) {
            FormulaManager.InvalidateFacts(control.table.key);
            await ChangesFieldValue(
                control.table.changes,
                value,
                control.table.key,
                fields,
                opts.isEdit,
                opts.isNew,
                saveTableRow,
                reportErrors,
                control.table,
                undefined,
                formMethods,
                undefined,
                invokeSavedChange,
                activeTargets,
                opts.changeRuntime?.actions,
            );

            const subTables = [...(control.table.tables ?? [])];
            while (subTables.length > 0) {
                if (opts.isCurrent?.() === false) return;
                const subTable = subTables.shift()!;
                await ChangesFieldValue(
                    subTable.changes,
                    value,
                    subTable.key,
                    fields,
                    opts.isEdit,
                    opts.isNew,
                    saveTableRow,
                    reportErrors,
                    subTable,
                    undefined,
                    formMethods,
                    undefined,
                    invokeSavedChange,
                    activeTargets,
                    opts.changeRuntime?.actions,
                );
                subTables.push(...(subTable.tables ?? []));
            }
            return;
        }

        await ChangesFieldValue(
            control.changes,
            value,
            control.key,
            fields,
            opts.isEdit,
            opts.isNew,
            saveField,
            reportErrors,
            undefined,
            undefined,
            formMethods,
            undefined,
            invokeSavedChange,
            activeTargets,
            opts.changeRuntime?.actions,
        );
    };

    let pending = collectInvokeChangeControls(scheme).filter((control) => {
        const wantsNew = opts.isNew && control.invokeChangesOnNew && !opts.turnOff;
        const wantsEdit = opts.isEdit && control.invokeChangesOnEdit;
        if (!wantsNew && !wantsEdit) return false;
        const elem = fields[control.key];
        if (!elem || elem.index === undefined || elem.index < 0) return false;
        // Содержимое таблицы-линков не в форме: едет отдельным запросом (pending) или запрос
        // отказал (loadFailed). Каскад по заглушке [] посчитал бы <change>/set-правила по
        // пустой таблице и записал бы нули в поля документа. Для pending это лишь порядок
        // (вызывающий ждёт догрузку), для отказа — единственный гард.
        return !elem.pending && !elem.loadFailed;
    });

    for (let sweep = 0; sweep < SWEEP_LIMIT && pending.length > 0; sweep++) {
        const hidden: InvokeChangeControl[] = [];
        for (const control of pending) {
            if (opts.isCurrent?.() === false) return;

            try {
                let visible = true;
                for (const rule of control.visibilityChain) {
                    if (!(await evaluator.evalBool(rule, true))) {
                        visible = false;
                        break;
                    }
                }
                if (visible && control.visibilityRules) {
                    visible = await evaluator.evalBool(control.visibilityRules, true);
                }
                if (opts.isCurrent?.() === false) return;
                if (!visible) {
                    hidden.push(control);
                    continue;
                }

                opts.onExecuted?.(control.key);
                await runCascade(control);
            } catch (error) {
                console.error('runInvokeChangesInit: ошибка каскада для', control.key, error);
            }
        }
        if (hidden.length === pending.length) break;
        pending = hidden;
    }
}
